import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import MenuBookIcon from '@mui/icons-material/MenuBook';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import CircularProgress from '@mui/material/CircularProgress';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { AppPagination } from '../../../ui/AppPagination';
import { CustomCheckbox } from "../../../ui/CustomCheckbox";
import { DatePickerField } from "../../../ui/DatePickerField";

type ApiProgramRead = {
  id: number;
  name: string;
  duration_hours: number;
  is_active: boolean;
  external_url: string | null;
  platform_program_id: string | null;
  created_at: string;
  updated_at: string;
};

type ApiProgramListResponse = {
  total: number;
  page: number;
  size: number;
  pages: number;
  items: ApiProgramRead[];
};

export type Program = {
  id: string;
  name: string;
  durationHours: number;
  isActive: boolean;
  externalUrl?: string;
  createdAt: string;
  updatedAt: string;
};

export type ProgramAssignment = {
  program: Program;
  theoretical_start_date?: string;
  theoretical_end_date?: string;
  exam_date?: string;
};

type ProgramExtraDetails = {
  theoretical_start_date: Date | null;
  theoretical_end_date: Date | null;
  exam_date: Date | null;
};

type Props = {
  onSelect?: (programs: Program[]) => void;
  onApplyToAll?: (assignments: ProgramAssignment[]) => void;
  onProgramsLoad?: (programs: Program[]) => void;
  initialSelectedIds?: Set<string>;
  initialSelectedPrograms?: Program[];
  initialProgramDetails?: Record<string, Partial<ProgramExtraDetails>>;
  hasStudents?: boolean;
  studentsVersion?: number;
};

const PAGE_SIZE = 10;
const CATALOG_PAGE_SIZE = 200;
const CATALOG_MAX_PAGES = 50;
const PROGRAMS_URL = `${process.env.REACT_APP_API_BASE_URL}/api/v1/programs/`;

const mapApiToUi = (p: ApiProgramRead): Program => ({
  id: String(p.id),
  name: p.name,
  durationHours: p.duration_hours,
  isActive: p.is_active,
  externalUrl: p.external_url ?? undefined,
  createdAt: p.created_at,
  updatedAt: p.updated_at,
});

const ProgramDetailPanel: React.FC<{
  program: Program;
  details: Partial<ProgramExtraDetails>;
  onDateChange: (
    programId: string,
    field: keyof ProgramExtraDetails,
    date: Date | null
  ) => void;
}> = ({ program, details, onDateChange }) => {
  const tStart = details.theoretical_start_date ?? null;
  const tEnd = details.theoretical_end_date ?? null;
  const examDate = details.exam_date ?? null;

  return (
    <div className="bg-gray-50 border-l border-r border-b border-gray-200 rounded-b-lg p-4 sm:p-6 space-y-6">
      <div>
        <h4 className="text-md font-semibold text-gray-700 mb-3">Теория</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <DatePickerField
            label="Начало"
            selected={tStart}
            onChange={(d: Date | null) =>
              onDateChange(program.id, "theoretical_start_date", d)
            }
          />
          <DatePickerField
            label="Окончание"
            selected={tEnd}
            onChange={(d: Date | null) =>
              onDateChange(program.id, "theoretical_end_date", d)
            }
          />
        </div>
      </div>
      <div>
        <h4 className="text-md font-semibold text-gray-700 mb-3">Экзамен</h4>
        <DatePickerField
          label="Дата экзамена"
          selected={examDate}
          onChange={(d: Date | null) => onDateChange(program.id, "exam_date", d)}
        />
      </div>
    </div>
  );
};

export const ProgramsManager: React.FC<Props> = ({
  onSelect,
  onApplyToAll,
  onProgramsLoad,
  initialSelectedIds,
  initialSelectedPrograms,
  initialProgramDetails,
  hasStudents = false,
  studentsVersion = 0,
}) => {
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");

  const [allPrograms, setAllPrograms] = useState<Program[]>(initialSelectedPrograms || []);
  const [pagePrograms, setPagePrograms] = useState<Program[]>([]);
  const [allSearchResults, setAllSearchResults] = useState<Program[]>([]);

  const [page, setPage] = useState<number>(1);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(1);

  const [loading, setLoading] = useState<boolean>(true);
  const [loadError, setLoadError] = useState<string>("");

  const [selectedProgramIds, setSelectedProgramIds] = useState<Set<string>>(
    initialSelectedIds || new Set()
  );
  const [programDetails, setProgramDetails] = useState<
    Record<string, Partial<ProgramExtraDetails>>
  >(initialProgramDetails || {});

  const [isDirty, setIsDirty] = useState<boolean>(false);

  // Восстановление считается активным, пока в allPrograms нет всех initialSelectedIds
  const [isRestoring, setIsRestoring] = useState<boolean>(true);

  const isInitialMount = useRef(true);
  const prevStudentsVersionRef = useRef<number>(studentsVersion);
  const justAppliedRef = useRef<boolean>(false);
  const hasFetchedMissing = useRef<boolean>(false);

  // Синхронизация initialSelectedIds -> state
  useEffect(() => {
    if (initialSelectedIds) {
      setSelectedProgramIds(initialSelectedIds);
    }
  }, [initialSelectedIds]);

  // Синхронизация initialSelectedPrograms -> allPrograms (предотвращает пустые списки)
  useEffect(() => {
    if (initialSelectedPrograms && initialSelectedPrograms.length > 0) {
      setAllPrograms(prev => {
        const map = new Map(prev.map(p => [p.id, p]));
        initialSelectedPrograms.forEach(p => map.set(p.id, p));
        return Array.from(map.values());
      });
    }
  }, [initialSelectedPrograms]);

  // Контроль завершения стадии восстановления
  useEffect(() => {
    if (!selectedProgramIds || selectedProgramIds.size === 0) {
      setIsRestoring(false);
      return;
    }

    const hasAll = Array.from(selectedProgramIds).every(id =>
      allPrograms.some(p => p.id === id)
    );

    if (hasAll) {
      setIsRestoring(false);
    }
  }, [selectedProgramIds, allPrograms]);

  const executeSearch = () => {
    setAppliedSearch(search);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      executeSearch();
    }
  };

  const handleClearSearch = () => {
    setSearch("");
    setAppliedSearch("");
  };

  // Метод автоматической дозагрузки недостающих программ по API
  useEffect(() => {
    const fetchMissingPrograms = async () => {
      if (hasFetchedMissing.current || !selectedProgramIds || selectedProgramIds.size === 0) {
        return;
      }
      const token = localStorage.getItem("token");
      if (!token) return;

      const missingIds = Array.from(selectedProgramIds).filter(
        (id) => !allPrograms.some((p) => p.id === id)
      );

      if (missingIds.length === 0) {
        hasFetchedMissing.current = true;
        return;
      }

      try {
        const fetchedPrograms = await Promise.all(
          missingIds.map(async (id) => {
            const res = await fetch(`${PROGRAMS_URL}${id}`, {
              headers: {
                Accept: "application/json",
                Authorization: token,
              },
            });
            if (!res.ok) throw new Error(`Could not fetch program ${id}`);
            const data: ApiProgramRead = await res.json();
            return mapApiToUi(data);
          })
        );

        setAllPrograms((prev) => {
          const map = new Map(prev.map((p) => [p.id, p]));
          fetchedPrograms.forEach((p) => {
            if (p) map.set(p.id, p);
          });
          return Array.from(map.values());
        });
        hasFetchedMissing.current = true;
      } catch (error) {
        console.error("Ошибка автоматического восстановления программ:", error);
      }
    };

    fetchMissingPrograms();
  }, [selectedProgramIds, allPrograms]);

  useEffect(() => {
    if (studentsVersion !== prevStudentsVersionRef.current) {
      prevStudentsVersionRef.current = studentsVersion;
      if (justAppliedRef.current) {
        justAppliedRef.current = false;
        return;
      }
      setIsDirty(true);
    }
  }, [studentsVersion]);

  // 1. Загрузка стандартного постраничного списка (без поиска)
  const fetchPage = useCallback(
    async (targetPage: number) => {
      const token = localStorage.getItem("token");
      if (!token) return;

      setLoading(true);
      setLoadError("");

      try {
        const params = new URLSearchParams({
          page: String(targetPage),
          size: String(PAGE_SIZE),
        });

        const res = await fetch(`${PROGRAMS_URL}?${params}`, {
          headers: {
            Accept: "application/json",
            Authorization: token,
          },
        });

        if (!res.ok) {
          throw new Error(`Ошибка загрузки: ${res.status} ${res.statusText}`);
        }

        const data: ApiProgramListResponse = await res.json();
        const mapped = (data.items ?? []).filter((p) => p.is_active).map(mapApiToUi);

        setPagePrograms(mapped);
        setTotalItems(data.total);
        setTotalPages(data.pages);

        setAllPrograms((prev) => {
          const map = new Map(prev.map((p) => [p.id, p]));
          mapped.forEach((p) => map.set(p.id, p));
          return Array.from(map.values());
        });
      } catch (e: any) {
        console.error("API Error:", e);
        setLoadError(e?.message || "Не удалось загрузить список программ");
        setPagePrograms([]);
      } finally {
        setLoading(false);
        setTimeout(() => {
          isInitialMount.current = false;
        }, 100);
      }
    },
    []
  );

  // 2. Метод для загрузки результатов поиска с бэкенда
  const fetchSearchResults = useCallback(
    async (queryText: string) => {
      const token = localStorage.getItem("token");
      if (!token) return;

      setLoading(true);
      setLoadError("");

      try {
        const params = new URLSearchParams({
          q: queryText.trim(),
          limit: "100",
        });

        const res = await fetch(`${PROGRAMS_URL}search?${params}`, {
          headers: {
            Accept: "application/json",
            Authorization: token,
          },
        });

        if (!res.ok) {
          throw new Error(`Ошибка поиска: ${res.status} ${res.statusText}`);
        }

        const data = await res.json();
        let mapped: Program[] = [];

        if (Array.isArray(data)) {
          mapped = data.filter((p: ApiProgramRead) => p.is_active).map(mapApiToUi);
        } else if (data && Array.isArray(data.items)) {
          mapped = data.items.filter((p: ApiProgramRead) => p.is_active).map(mapApiToUi);
        }

        setAllSearchResults(mapped);
        setTotalItems(mapped.length);
        setTotalPages(Math.ceil(mapped.length / PAGE_SIZE));

        setPagePrograms(mapped.slice(0, PAGE_SIZE));

        setAllPrograms((prev) => {
          const map = new Map(prev.map((p) => [p.id, p]));
          mapped.forEach((p) => map.set(p.id, p));
          return Array.from(map.values());
        });
      } catch (e: any) {
        console.error("Search API Error:", e);
        setLoadError(e?.message || "Не удалось выполнить поиск программ");
        setAllSearchResults([]);
        setPagePrograms([]);
        setTotalItems(0);
        setTotalPages(1);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // Полный каталог активных программ для комбобокса (не зависит от UI-пагинации)
  const fetchFullCatalog = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token) return;

    try {
      const catalog: Program[] = [];
      let pageNum = 1;
      let total = Infinity;

      while (pageNum <= CATALOG_MAX_PAGES && catalog.length < total) {
        const params = new URLSearchParams({
          page: String(pageNum),
          size: String(CATALOG_PAGE_SIZE),
        });

        const res = await fetch(`${PROGRAMS_URL}?${params}`, {
          headers: {
            Accept: "application/json",
            Authorization: token,
          },
        });

        if (!res.ok) {
          throw new Error(`Ошибка загрузки каталога: ${res.status} ${res.statusText}`);
        }

        const data: ApiProgramListResponse = await res.json();
        const mapped = (data.items ?? []).filter((p) => p.is_active).map(mapApiToUi);
        catalog.push(...mapped);
        total = typeof data.total === "number" ? data.total : catalog.length;

        if ((data.items ?? []).length === 0) break;
        pageNum += 1;
      }

      setAllPrograms((prev) => {
        const map = new Map(prev.map((p) => [p.id, p]));
        catalog.forEach((p) => map.set(p.id, p));
        return Array.from(map.values());
      });

      onProgramsLoad?.(catalog);
    } catch (e: any) {
      console.error("Catalog API Error:", e);
    }
  }, [onProgramsLoad]);

  useEffect(() => {
    fetchFullCatalog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setPage(1);
    if (appliedSearch) {
      fetchSearchResults(appliedSearch);
    } else {
      fetchPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedSearch]);

  useEffect(() => {
    if (isInitialMount.current) return;

    if (appliedSearch) {
      const startIndex = (page - 1) * PAGE_SIZE;
      const slice = allSearchResults.slice(startIndex, startIndex + PAGE_SIZE);
      setPagePrograms(slice);
    } else {
      fetchPage(page);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  // Умная сортировка и размещение выбранных элементов в самом верху списка на Page 1
  const displayedPrograms = useMemo(() => {
    const selected = allPrograms.filter((p) => selectedProgramIds.has(p.id));

    if (appliedSearch) {
      return [...pagePrograms].sort((a, b) => {
        const aChecked = selectedProgramIds.has(a.id);
        const bChecked = selectedProgramIds.has(b.id);
        if (aChecked && !bChecked) return -1;
        if (!aChecked && bChecked) return 1;
        return 0;
      });
    }

    if (page === 1) {
      const nonSelected = pagePrograms.filter((p) => !selectedProgramIds.has(p.id));
      return [...selected, ...nonSelected];
    } else {
      return pagePrograms.filter((p) => !selectedProgramIds.has(p.id));
    }
  }, [pagePrograms, allPrograms, selectedProgramIds, page, appliedSearch]);

  // Обратный вызов к родителю БЛОКИРУЕТСЯ во время восстановления
  useEffect(() => {
    if (isRestoring) {
      return;
    }

    if (onSelect) {
      const selected = allPrograms.filter((p) => selectedProgramIds.has(p.id));
      onSelect(selected);
    }
  }, [selectedProgramIds, allPrograms, onSelect, isRestoring]);

  const handleProgramToggle = useCallback((programId: string) => {
    setSelectedProgramIds((prev) => {
      const next = new Set(prev);
      if (next.has(programId)) {
        next.delete(programId);
      } else {
        next.add(programId);
      }
      return next;
    });
    if (!isInitialMount.current) {
      setIsDirty(true);
    }
  }, []);

  const handleProgramDateChange = useCallback(
    (programId: string, field: keyof ProgramExtraDetails, date: Date | null) => {
      setProgramDetails((prev) => ({
        ...prev,
        [programId]: { ...prev[programId], [field]: date },
      }));
      if (!isInitialMount.current) {
        setIsDirty(true);
      }
    },
    []
  );

  const toIsoDate = (date?: Date | null) => {
    if (!date) return undefined;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const handleApplyToAll = useCallback(() => {
    if (!onApplyToAll || selectedProgramIds.size === 0 || !hasStudents) return;

    const assignments = Array.from(selectedProgramIds).reduce<ProgramAssignment[]>(
      (acc, programId) => {
        const program = allPrograms.find((p) => p.id === programId);
        if (!program) return acc;

        const details = programDetails[programId] || {};
        acc.push({
          program,
          theoretical_start_date: toIsoDate(details.theoretical_start_date ?? null),
          theoretical_end_date: toIsoDate(details.theoretical_end_date ?? null),
          exam_date: toIsoDate(details.exam_date ?? null),
        });
        return acc;
      },
      []
    );

    justAppliedRef.current = true;
    setIsDirty(false);
    onApplyToAll(assignments);
  }, [onApplyToAll, selectedProgramIds, allPrograms, programDetails, hasStudents]);

  const isButtonEnabled = hasStudents && selectedProgramIds.size > 0 && isDirty;

  const getButtonTooltip = (): string => {
    if (!hasStudents) return "Сначала добавьте студента";
    if (selectedProgramIds.size === 0) return "Выберите хотя бы одну программу";
    if (!isDirty) return "Изменения уже применены";
    return "";
  };

  const buttonTooltip = getButtonTooltip();

  return (
    <section className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden w-full">
      <header className="flex items-center gap-3 p-4 sm:p-5 border-b border-gray-200">
        <MenuBookIcon className="text-gray-500" />
        <h1 className="text-lg font-medium text-gray-900">Программы обучения</h1>
      </header>

      <div className="p-4 sm:p-6 space-y-4">
        {/* Поисковая панель */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              <SearchIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Поиск по программам..."
              className="w-full rounded-md border-gray-300 bg-white py-2 pl-10 pr-10 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            />
            {search && (
              <button
                type="button"
                onClick={handleClearSearch}
                className="absolute inset-y-0 right-0 flex items-center pr-3"
              >
                <ClearIcon className="h-5 w-5 text-gray-400 hover:text-gray-600" />
              </button>
            )}
          </div>
          <button
            type="button"
            onClick={executeSearch}
            disabled={loading}
            className="flex items-center justify-center px-6 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-sm transition disabled:opacity-50"
          >
            <SearchIcon fontSize="small" className="mr-2" />
            Найти
          </button>
        </div>

        <div className="space-y-3">
          {loading ? (
            <div className="flex justify-center p-12">
              <CircularProgress />
            </div>
          ) : loadError ? (
            <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">
              {loadError}
            </div>
          ) : displayedPrograms.length === 0 ? (
            <div className="rounded-lg border-2 border-dashed border-gray-300 p-12 text-center">
              <h3 className="text-lg font-medium text-gray-500">
                {appliedSearch ? "Ничего не найдено" : "Программы отсутствуют"}
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                {appliedSearch
                  ? "Попробуйте изменить запрос."
                  : "Нет доступных для выбора программ."}
              </p>
            </div>
          ) : (
            <>
              {displayedPrograms.map((program: Program) => {
                const isChecked = selectedProgramIds.has(program.id);
                return (
                  <div key={program.id}>
                    <button
                      type="button"
                      onClick={() => handleProgramToggle(program.id)}
                      className={`w-full text-left border p-4 shadow-sm transition-all flex items-center gap-4 ${
                        isChecked
                          ? "bg-blue-50 border-blue-400 shadow-md rounded-t-lg"
                          : "bg-white border-gray-200 hover:border-gray-300 hover:shadow rounded-lg"
                      }`}
                    >
                      <CustomCheckbox
                        id={`program-check-${program.id}`}
                        checked={isChecked}
                        onChange={() => {}}
                        className="pointer-events-none"
                      />
                      <div className="flex-grow flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <MenuBookIcon
                            className={isChecked ? "text-blue-600" : "text-gray-400"}
                          />
                          <div className="flex flex-col">
                            <span
                              className={`font-medium ${
                                isChecked ? "text-blue-800" : "text-gray-800"
                              }`}
                            >
                              {program.name}
                            </span>
                            <span
                              className={`text-sm ${
                                isChecked ? "text-blue-600" : "text-gray-500"
                              }`}
                            >
                              {program.durationHours} ч.
                            </span>
                          </div>
                        </div>
                        <ChevronRightIcon
                          className={`transition-transform duration-200 ${
                            isChecked ? "rotate-90 text-blue-600" : "text-gray-400"
                          }`}
                        />
                      </div>
                    </button>

                    {isChecked && (
                      <ProgramDetailPanel
                        program={program}
                        details={programDetails[program.id] || {}}
                        onDateChange={handleProgramDateChange}
                      />
                    )}
                  </div>
                );
              })}

              {totalPages > 1 && (
                <AppPagination
                  page={page}
                  totalItems={totalItems}
                  pageSize={PAGE_SIZE}
                  onChange={setPage}
                  className="pt-2"
                />
              )}
            </>
          )}
        </div>
      </div>

      <div className="p-4 sm:p-5 border-t border-gray-200 bg-gray-50 flex justify-between items-center">
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <MenuBookIcon className="text-gray-400" fontSize="small" />
          <span className="font-medium">
            Всего программ:{" "}
            <span className="text-blue-600 font-semibold">{totalItems}</span>
          </span>
        </div>

        <div className="relative group inline-block">
          <button
            type="button"
            onClick={handleApplyToAll}
            disabled={!isButtonEnabled}
            className={`inline-flex items-center justify-center gap-2 rounded-md px-4 py-2 text-sm font-semibold shadow-sm transition-colors ${
              !isButtonEnabled
                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                : "bg-blue-600 text-white hover:bg-blue-700 active:bg-blue-800"
            }`}
          >
            <ContentCopyIcon fontSize="small" />
            Применить выбранные
          </button>
          {!isButtonEnabled && buttonTooltip && (
            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-1.5 bg-gray-800 text-white text-xs rounded-md whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-10">
              {buttonTooltip}
              <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-800"></div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};